import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import type { DropdownMenuCheckboxItemProps } from "@radix-ui/react-dropdown-menu"
import { ChevronDown } from "lucide-react"
import type { Course } from "@/modules/courses/entities/courses"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import useCoursesInfo from "@/modules/courses/hooks/use-courses-info"

type Checked = DropdownMenuCheckboxItemProps["checked"]

interface FilterWeeklyObjectiveByCourseProps {
  courses: Course[]
  onChange?: (selectedCourses: Course[]) => void
}

export default function FilterWeeklyObjectiveByCourse({
  courses,
  onChange,
}: FilterWeeklyObjectiveByCourseProps) {
  const { getCourseColor } = useCoursesInfo()
  const [selectedCourses, setSelectedCourses] = useState<Record<string, Checked>>({})

  useEffect(() => {
    const initialSelection = courses.reduce(
      (acc, course) => {
        acc[course.id] = true
        return acc
      },
      {} as Record<string, Checked>,
    )
    setSelectedCourses(initialSelection)
    onChange?.(courses)
  }, [courses])

  const handleCheckedChange = (course: Course, checked: Checked) => {
    setSelectedCourses((prev) => {
      const updated = { ...prev, [course.id]: checked }
      const selected = courses.filter((c) => updated[c.id])
      onChange?.(selected)
      return updated
    })
  }

  const getInitial = (name: string) => {
    const words = name.split(" ")
    return words
      .map((w) => w.charAt(0).toUpperCase())
      .join("")
      .slice(0, 1)
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button className="border-0" variant="outline">
          Materias <ChevronDown className="ml-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56">
        {courses.map((course) => (
          <DropdownMenuCheckboxItem
            key={course.id}
            checked={selectedCourses[course.id] ?? false}
            onCheckedChange={(checked) => handleCheckedChange(course, checked)}
            className="flex items-center gap-2"
          >
            <Avatar className="h-6 w-6">
              <AvatarFallback
                style={{
                  background: getCourseColor(course.subjectId),
                }}
                className="text-xs font-medium text-white"
              >
                {getInitial(course.name)}
              </AvatarFallback>
            </Avatar>
            {course.name}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
